import React from 'react';
import { Check, X } from 'lucide-react';
import { SERVICE_ICON_OPTIONS } from '../utils/serviceIcons';
import ModalShell from './ModalShell';
import { haptic } from '../utils/vibration';

/**
 * Модальное окно выбора иконки
 * @param {boolean} open - Открыто ли окно
 * @param {function} onClose - Закрыть окно
 * @param {function} onSelect - Выбрать иконку
 * @param {string} selectedIcon - Выбранная иконка
 * @param {string} iconColor - Цвет иконки (blue, green, purple, etc.)
 */
const IconPickerModal = ({
  open,
  onClose,
  onSelect,
  selectedIcon = null,
  iconColor = 'blue',
  title = 'Выберите иконку',
  iconOptions = SERVICE_ICON_OPTIONS
}) => {
  const handleSelect = (iconName) => {
    haptic('selection');
    onSelect(iconName);
    onClose();
  };

  const closeWithHaptic = () => {
    haptic('close');
    onClose();
  };

  return (
    <ModalShell
      open={open}
      onClose={closeWithHaptic}
      variant="sheet"
      // Иконки открываются поверх формы услуги, поэтому слой должен быть выше
      // глобального .modal-overlay-backdrop с z-index: 9999.
      zIndexClass="!z-[10000]"
      containerClassName="sm:!items-end"
      panelClassName="max-h-[72dvh] overflow-y-auto sm:max-w-lg sm:!rounded-b-none"
      swipeToClose
    >
        {/* Заголовок */}
        <div className="sticky top-0 bg-white dark:bg-gray-900 p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between z-10">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
            {title}
          </h3>
          <button onClick={closeWithHaptic} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <div className="p-4">
          <button
            type="button"
            onClick={() => handleSelect(null)}
            className="mb-4 w-full rounded-xl border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            Без иконки
          </button>

          {/* Сетка иконок */}
          <div className="grid grid-cols-5 gap-3 sm:grid-cols-6">
            {iconOptions.map((item) => {
              const IconComp = item.icon;
              const isSelected = selectedIcon === item.value;
              return (
                <button
                  key={item.value}
                  onClick={() => handleSelect(item.value)}
                  className={`relative flex aspect-square items-center justify-center rounded-2xl border transition-all ${
                    isSelected
                      ? iconColor === 'purple' ? 'border-purple-500 bg-purple-600 text-white scale-[1.03] shadow-lg shadow-purple-500/20' :
                        iconColor === 'green' ? 'border-green-500 bg-green-600 text-white scale-[1.03] shadow-lg shadow-green-500/20' :
                        'border-blue-500 bg-blue-600 text-white scale-[1.03] shadow-lg shadow-blue-500/20'
                      : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-300 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-gray-600 dark:hover:bg-gray-700'
                  }`}
                  title={item.label}
                  aria-label={item.label}
                >
                  {IconComp ? <IconComp className="h-7 w-7 shrink-0" /> : null}
                  {isSelected ? (
                    <span className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                      <Check className="h-3.5 w-3.5 shrink-0" />
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>

        </div>
    </ModalShell>
  );
};

export default IconPickerModal;
